// Hand-authored paddock templates. Seeded variation happens inside `build`.
//
// Every template must pass the Big Dog bot solvability gate (`npm run bot`) before it ships, and
// its `parSeconds` / `parBarks` come from the bot's measured run, not from guesswork:
// `npm run bot -- --template <id> --seeds 40` prints the line to paste.
//
// Geometry rules (gate capacity, the 8m gap floor, spawn clearances) are enforced by
// templates.test.ts and explained in the `level-authoring` skill. Read it before adding a template.
//
// ONE RULE THE TESTS CANNOT CHECK: every interior fence must leave at least one END open. A fence
// anchored wall-to-wall with only an interior gap is beyond the bot — it drives on the flock's
// centroid in a straight line and cannot route around an obstruction, so any flock that ends up on
// the wrong side of the wall wedges into the corner and never comes back. A 'narrow-neck' template
// (a 12m pinch spanning the full width) was written, validated, and cut for exactly this: 39/40 on
// two different spawn layouts, always the same picture — the mob jammed in a corner south of the
// fence with one sheep stranded north of it. split-race and dogleg work because both leave an end
// open. Revisit if the bot ever learns waypoints; see PLAN.md § M4.
//
// PHASE 3: this is a starter pair to exercise the loader and the bot. The real set wants ~8-12
// templates graded by bot-par so the week has a difficulty curve.

import { rngInt, rngRange } from './seed'
import { vec } from './vec2'
import type { FenceSegment, Level } from './types'

/**
 * The paddock is portrait, not square.
 *
 * A 100x100 world wasted ~60% of a portrait phone screen — the paddock rendered small and central
 * with dead space above and below, which is also what made it hard to tell the playfield from the
 * surround. 70x120 is close to a phone's aspect after browser chrome (0.58 vs ~0.56), so the
 * paddock fills the screen and sheep get roughly 40% more pixels each at the same flock size.
 *
 * Desktop letterboxes horizontally instead, which is the accepted trade: the game is played on a
 * phone, and the renderer already fits the world by the tighter axis.
 *
 * The gate goes on a SHORT side, so the drive runs the long axis of the paddock. That reads as
 * "work the flock up the paddock" and gives the dog room to swing wide behind the mob.
 */
const W = 70
const H = 120

/**
 * WHERE THINGS START. The mob is at the far end, the handler stands at the gate, and the dog starts
 * **with the handler** — not behind the mob.
 *
 * This is how stock is actually worked, and it is what gives the outrun a reason to exist: the run
 * opens with the dog at the wrong end of the paddock, and getting it round behind the mob is the
 * player's first problem rather than a position they are handed. Before handler commands existed
 * the dog spawned behind the flock, which made sense when the player dragged it directly and the
 * cast was not a skill.
 *
 * Two constraints survive from that arrangement, both still enforced by `templates.test.ts`:
 * the dog must start further than `dogRadius` from the flock, so no seed opens under pressure it
 * did not earn; and it must start inside the world.
 *
 * Note each `dogSpawn` still makes exactly TWO `rng` draws. The stream's shape is part of every
 * daily's identity (see the level-authoring skill §1) — changing the ranges re-rolls nothing,
 * adding or removing a draw would re-roll everything.
 */

export interface Template {
  id: string
  name: string
  /** 1 (gentlest) to 5 (hardest). Drives the week's curve in `daily.ts`, never the score. */
  difficulty: number
  build(rng: () => number): Level
}

/** Perimeter fence with a gap where the gate sits. */
function perimeter(gapSide: 'n' | 's' | 'e' | 'w', gapStart: number, gapWidth: number): FenceSegment[] {
  const segs: FenceSegment[] = []
  const push = (ax: number, ay: number, bx: number, by: number) =>
    segs.push({ a: vec(ax, ay), b: vec(bx, by) })

  const side = (
    s: 'n' | 's' | 'e' | 'w',
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ) => {
    if (s !== gapSide) {
      push(ax, ay, bx, by)
      return
    }
    // Split the side around the gate gap.
    if (s === 'n' || s === 's') {
      push(ax, ay, gapStart, by)
      push(gapStart + gapWidth, ay, bx, by)
    } else {
      push(ax, ay, bx, gapStart)
      push(ax, gapStart + gapWidth, bx, by)
    }
  }

  side('n', 0, 0, W, 0)
  side('s', 0, H, W, H)
  side('w', 0, 0, 0, H)
  side('e', W, 0, W, H)
  return segs
}

export const TEMPLATES: Template[] = [
  {
    id: 'open-paddock',
    name: 'Open Paddock',
    difficulty: 1,
    build(rng) {
      const gapStart = rngRange(rng, 22, 36)
      const gapWidth = 12
      return {
        id: 'open-paddock',
        name: 'Open Paddock',
        world: { w: W, h: H },
        fences: perimeter('n', gapStart, gapWidth),
        // A 12m-deep pocket inside the north wall. Both dimensions clear 2*contentRadius(18)+2 =
        // 9.8m, the size a settled flock of 18 needs before separation stops it ever settling.
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 12, 30), y: rngRange(rng, 62, 78), w: 24, h: 22 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 12, 18),
        // Measured: command-bot median 44.9s over 40 seeds (min 26.8, max 87.6), +15% headroom.
        // Re-measured after the walk-on pass (pace, goal-aim, fence line), which took the median
        // and the tail down together on nearly every template.
        parSeconds: 52,
        // NOT measurable from the bot: median 0 barks over 40 seeds — an open paddock does not
        // require any. Placeholder until playtest; see PLAN.md § M5.
        parBarks: 6,
        difficulty: 1,
      }
    },
  },
  {
    id: 'split-race',
    name: 'Split Race',
    difficulty: 2,
    build(rng) {
      const gapStart = rngRange(rng, 26, 36)
      const gapWidth = 12
      const fences = perimeter('n', gapStart, gapWidth)
      // A dividing fence across the paddock with a gap the flock must be worked through. It stops
      // 10m short of each side wall rather than meeting them: the ends are escape routes the flock
      // will take if pushed carelessly, which is what makes the gap a choice instead of a funnel.
      const raceY = rngRange(rng, 55, 70)
      const gapX = rngRange(rng, 26, 44)
      fences.push({ a: vec(10, raceY), b: vec(gapX - 8, raceY) })
      fences.push({ a: vec(gapX + 8, raceY), b: vec(W - 10, raceY) })
      return {
        id: 'split-race',
        name: 'Split Race',
        world: { w: W, h: H },
        fences,
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 16, 34), y: rngRange(rng, 80, 92), w: 18, h: 16 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 10, 14),
        // Measured: command-bot median 36.5s over 40 seeds (min 28.1, max 88.0), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 42,
        // NOT measurable from the bot: median 0 barks over 40 seeds. Placeholder until playtest;
        // see PLAN.md § M5.
        parBarks: 9,
        difficulty: 2,
      }
    },
  },
  {
    id: 'funnel',
    name: 'The Funnel',
    difficulty: 1,
    build(rng) {
      const gapStart = rngRange(rng, 24, 34)
      const gapWidth = 12
      const fences = perimeter('n', gapStart, gapWidth)
      // Two long rails forming a 34m race up to the gate. Wide enough that the flock is never
      // squeezed, so this is about lining the mob up rather than threading anything.
      fences.push({ a: vec(18, 20), b: vec(18, 50) })
      fences.push({ a: vec(52, 20), b: vec(52, 50) })
      return {
        id: 'funnel',
        name: 'The Funnel',
        world: { w: W, h: H },
        fences,
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 14, 34), y: rngRange(rng, 66, 78), w: 22, h: 20 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 12, 16),
        // Measured: command-bot median 40.8s over 40 seeds (min 26.1, max 92.1), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 47,
        parBarks: 6,
        difficulty: 1,
      }
    },
  },
  {
    id: 'island',
    name: 'The Island',
    difficulty: 3,
    build(rng) {
      const gapStart = rngRange(rng, 24, 34)
      const gapWidth = 12
      const fences = perimeter('n', gapStart, gapWidth)
      // A closed block mid-paddock. The flock splits around it if driven straight at it, which is
      // the mistake the layout is built to punish — 14m a side, so both flanks are real routes.
      fences.push({ a: vec(28, 50), b: vec(42, 50) })
      fences.push({ a: vec(42, 50), b: vec(42, 64) })
      fences.push({ a: vec(42, 64), b: vec(28, 64) })
      fences.push({ a: vec(28, 64), b: vec(28, 50) })
      return {
        id: 'island',
        name: 'The Island',
        world: { w: W, h: H },
        fences,
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 14, 34), y: rngRange(rng, 72, 84), w: 22, h: 18 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 12, 16),
        // Measured: command-bot median 42.8s over 40 seeds (min 28.4, max 91.5), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 49,
        parBarks: 6,
        difficulty: 3,
      }
    },
  },
  {
    id: 'long-drive',
    name: 'The Long Drive',
    difficulty: 4,
    build(rng) {
      const gapStart = rngRange(rng, 22, 36)
      const gapWidth = 12
      // No obstacles at all. The difficulty is the flock: up to 22 sheep spawned at the far end,
      // which is a cohesion problem rather than a routing one — a big mob strings out over 100m.
      return {
        id: 'long-drive',
        name: 'The Long Drive',
        world: { w: W, h: H },
        fences: perimeter('n', gapStart, gapWidth),
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 20, 34), y: rngRange(rng, 82, 94), w: 26, h: 22 },
        // West, not south: the spawn rect reaches y 116 and there is no room left against the wall.
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 18, 22),
        // Measured: command-bot median 37.2s over 40 seeds (min 31.6, max 80.7), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 43,
        parBarks: 8,
        difficulty: 4,
      }
    },
  },
  {
    id: 'dogleg',
    name: 'The Dogleg',
    difficulty: 4,
    build(rng) {
      const gapStart = rngRange(rng, 24, 34)
      const gapWidth = 12
      const fences = perimeter('n', gapStart, gapWidth)
      // Two offset walls: the flock must be worked right around the first and left around the
      // second. Each is anchored to opposite side walls, so neither can be short-cut.
      fences.push({ a: vec(0, 45), b: vec(40, 45) })
      fences.push({ a: vec(30, 75), b: vec(W, 75) })
      return {
        id: 'dogleg',
        name: 'The Dogleg',
        world: { w: W, h: H },
        fences,
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 16, 34), y: rngRange(rng, 88, 98), w: 20, h: 16 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 10, 14),
        // Measured: command-bot median 63.1s over 40 seeds (min 36.0, max 140.5), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 73,
        parBarks: 10,
        difficulty: 4,
      }
    },
  },
  {
    id: 'chicane',
    name: 'The Chicane',
    difficulty: 5,
    build(rng) {
      const gapStart = rngRange(rng, 26, 34)
      const gapWidth = 12
      const fences = perimeter('n', gapStart, gapWidth)
      // Three staggered walls, alternating sides: a serpentine drive the whole length of the
      // paddock. The hardest layout in the set, and the one most likely to strand a sheep — which
      // is exactly what the bot's sidestep recovery exists for.
      fences.push({ a: vec(0, 40), b: vec(46, 40) })
      fences.push({ a: vec(24, 70), b: vec(W, 70) })
      fences.push({ a: vec(0, 95), b: vec(46, 95) })
      return {
        id: 'chicane',
        name: 'The Chicane',
        world: { w: W, h: H },
        fences,
        gate: { x: gapStart, y: 0, w: gapWidth, h: 12 },
        flockSpawn: { x: rngRange(rng, 18, 34), y: rngRange(rng, 102, 108), w: 18, h: 10 },
        dogSpawn: vec(rngRange(rng, gapStart, gapStart + gapWidth), rngRange(rng, 15, 20)),
        flockSize: rngInt(rng, 10, 14),
        // Measured: command-bot median 64.8s over 40 seeds (min 46.5, max 129.1), +15% headroom.
        // Re-measured after the P17 handling pass — gentler walk-on, longer
        // fright, and a dog that gets clear of the mob before it arcs.
        parSeconds: 75,
        parBarks: 12,
        difficulty: 5,
      }
    },
  },
]
